import { useState, useEffect, useCallback } from 'react'
import restClient from 'Shared/hooks/restClient'
import SqlCodeEditor from './SqlCodeEditor'
import type { RawSqlColumn, FactTable } from 'AnalyticsDashboard/utils/factTableHelpers'

interface RawSqlEditorProps {
  initialSql?: string
  // Called whenever the editor has a successfully-validated SQL. Passes the
  // detected columns + the backend-synthesised fact_tables_info-shape FactTable
  // (real segment dims etc.) so the parent can drive the slot UI off the
  // authoritative metadata.
  onValidated: (sql: string, columns: RawSqlColumn[], factTable: FactTable | null) => void
  // Called when the user has edited the SQL since the last successful validate,
  // so the parent can clear/disable any state that depended on the prior validate.
  onInvalidate?: () => void
}

interface ValidateSuccess {
  valid: true
  columns: RawSqlColumn[]
  factTable?: FactTable
}
interface ValidateFailure {
  valid: false
  error: string
}
type ValidateResponse = ValidateSuccess | ValidateFailure

export default function RawSqlEditor({ initialSql = '', onValidated, onInvalidate }: RawSqlEditorProps) {
  const [sql, setSql] = useState(initialSql)
  const [lastValidatedSql, setLastValidatedSql] = useState<string | null>(null)
  const [columns, setColumns] = useState<RawSqlColumn[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)

  const handleValidate = useCallback(async () => {
    if (!sql.trim()) return
    setValidating(true)
    setError(null)
    try {
      const { data } = await restClient.post<ValidateResponse>(
        '/api/analytics/analytics_queries/validate_raw_sql',
        { sql }
      )
      if (data.valid) {
        setColumns(data.columns)
        setLastValidatedSql(sql)
        onValidated(sql, data.columns, data.factTable || null)
      } else {
        setColumns(null)
        setLastValidatedSql(null)
        setError(data.error)
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to validate SQL')
      setColumns(null)
      setLastValidatedSql(null)
    } finally {
      setValidating(false)
    }
  }, [sql, onValidated])

  // If the user edits past the validated SQL, drop the cached columns so the
  // parent can re-disable Save until they validate again.
  useEffect(() => {
    if (lastValidatedSql !== null && sql !== lastValidatedSql) {
      setColumns(null)
      setLastValidatedSql(null)
      onInvalidate?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sql])

  const isValidated = lastValidatedSql !== null && sql === lastValidatedSql

  return (
    <div className="query-builder__section">
      <h3>Custom SQL</h3>
      <SqlCodeEditor
        value={sql}
        onChange={setSql}
        disabled={validating}
        placeholder="SELECT shard, client_id, campaign_id, created_at, ... FROM ..."
      />

      <div className="query-builder__raw-sql-actions">
        <button
          type="button"
          className="query-builder__ai-button"
          onClick={handleValidate}
          disabled={validating || !sql.trim()}
        >
          {validating ? 'Validating...' : isValidated ? '✓ Validated' : 'Validate'}
        </button>
      </div>

      {error && (
        <div className="query-builder__ai-error" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      {columns && (
        <div className="query-builder__raw-sql-columns">
          <h4>Detected columns</h4>
          <table>
            <thead>
              <tr><th>Name</th><th>Type</th><th>Binding</th></tr>
            </thead>
            <tbody>
              {columns.map(c => (
                <tr key={c.name}>
                  <td><code>{c.name}</code></td>
                  <td>{c.type}</td>
                  <td>
                    {c.entityKind && <>entity: <code>{c.entityKind}</code></>}
                    {c.time && <>primary time axis</>}
                    {!c.entityKind && !c.time && <span className="query-builder__optional">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="query-builder__optional" style={{ marginTop: 8, fontSize: 12 }}>
            To bind a column as a dashboard filter, name it with the canonical id
            (<code>campaign_id</code>, <code>target_id</code>, <code>client_id</code>, <code>flow_id</code>, <code>user_id</code>, <code>organisation_id</code>, <code>role_id</code>).
            For the time axis, name it <code>created_at</code> or <code>sent_at</code>.
          </p>
        </div>
      )}
    </div>
  )
}
